import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { Button, Message } from 'components';
import { FirebaseContext } from 'context';
import { AnimatePresence } from 'motion/react';

function MagicLinkLoginForm({ sessionId, onSuccess }) {
  const { firebase } = useContext(FirebaseContext);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [focused, setFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setMessage('');

    try {
      // Store email in localStorage for later use during sign in
      window.localStorage.setItem(`emailForSignIn-${sessionId}`, email);

      // Call Firebase function to send magic link
      await firebase.emails.sendMagicLink({
        email: email.toLowerCase().trim(),
        sessionId,
        origin: window.location.origin,
        href: window.location.href,
        emailData: {
          From: 'Congress Feedback Program <support@dsazengage.com>',
          ReplyTo: 'support@dsazengage.com',
          TemplateModel: {
            site_name: 'Congress Feedback Program'
          }
        }
      });

      setMessage(
        `Secure link sent to ${email}<br />Check your email to continue`
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setErrorMessage(
        err.message || 'Failed to send secure link to your email address.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h2>Sign in with Secure Link</h2>
      <p>
        Enter your email address and we&apos;ll send you a secure link to access
        this session.
      </p>
      <form onSubmit={handleSubmit}>
        <InputWrapper>
          <StyledInput
            id="magiclink-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="email"
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <StyledLabel htmlFor="magiclink-email" $float={focused || !!email}>
            Email address
          </StyledLabel>
        </InputWrapper>
        <Button
          type="submit"
          loading={loading}
          disabled={!email || loading}
          width="180px"
          changeColorWhenDisabled={false}
          shine={!loading}
          style={{ marginTop: '30px' }}>
          Send Secure Link
        </Button>
      </form>
      <AnimatePresence>
        {message && <Message type="success" message={message} />}
      </AnimatePresence>
      <AnimatePresence>
        {errorMessage && <Message type="error" message={errorMessage} />}
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.section`
  background-color: #fff;
  border-radius: var(--border-radius-md);
  margin: 0 auto;
  max-width: 28.375rem;
  padding: 3.25rem 1.875rem 2.5rem;
  perspective: 50rem;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (min-width: 1200px) {
    max-width: 28.375rem;
    padding: 3.25rem 1.875rem 2.5rem;
  }

  h2,
  p {
    text-align: center;
  }

  p {
    margin-bottom: 2.125rem;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1em;
    text-transform: uppercase;
  }

  form {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  label,
  input {
    width: 100%;
  }

  label {
    /* margin-bottom: 1.188rem; */
    width: fit-content;

    &:last-of-type {
      margin-bottom: 0.5rem;
    }
  }
`;

// Floating label animation styles
const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  /* margin-bottom: 1.188rem; */
`;

const StyledInput = styled.input`
  background: #fff;
  border: 1px solid var(--light-grey);
  border-radius: 4px;
  font-size: 1rem;
  padding: 1rem 0.75rem;
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const StyledLabel = styled.label`
  background: #fff;
  color: var(--light-brown);
  font-size: 1rem;
  left: 1.1rem;
  padding: 0 0.25rem;
  pointer-events: none;
  position: absolute;
  top: 1.15rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: fit-content;
  z-index: 2;

  ${({ $float }) =>
    $float &&
    `
      top: -1.25rem;
      left: 0rem;
      font-size: 0.78rem;
      color: var(--blue);
      background: #fff;
      padding: 0 0.25rem;
      z-index: 2;
    `}
`;

export default MagicLinkLoginForm;
